import React, {useEffect, useState, useMemo} from "react";
import axios from "../../AxiosInstance";
import styles from '../../styles/dashboard.module.css'
import Header from "../../components/header/header";
import DeviceModal from "../../components/device_modal/device_modal";
import ListView from "../machines/components/list_view";
import MapView from "../machines/components/map_view";
import PageTitle from "../../components/page_title/page_title";
import EmptyPlaceholder from "../../components/empty_placeholder/empty_placeholder";
import {useNavigate} from "react-router-dom";
import useMQTTMultiDevice from "../../components/mqtt_service/mqtt_multi_device";
import {useTranslation} from "react-i18next";
import {countAlertStates} from "../../utils/alertCounts";

export default function DevicesBoard() {
    const {t} = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [viewType, setViewType] = useState('list');
    const [data, setData] = useState([]);
    const [planRefreshRate, setPlanRefreshRate] = useState(900);
    const device_user_id = localStorage.getItem('device_user_id');
    const device_user = JSON.parse(localStorage.getItem('device_user'));
    const navigate = useNavigate();

    // Extraer device IDs para suscribirse via MQTT
    const deviceIds = useMemo(() => {
        return data.map(device => device.id).filter(Boolean);
    }, [data]);

    // Suscribirse a MQTT para actualizaciones en tiempo real
    const { data: mqttData } = useMQTTMultiDevice(deviceIds);

    // Actualizar devices cuando llegan datos por MQTT
    useEffect(() => {
        if (!mqttData || Object.keys(mqttData).length === 0) return;

        setData(prevData => {
            const updatedData = prevData.map(device => {
                const deviceVariables = mqttData[device.id];
                if (deviceVariables && Array.isArray(deviceVariables)) {
                    // Recalcular contadores con la regla compartida, la misma
                    // que aplica el backend. El if/else if anterior no contaba
                    // en ningún bucket las variables sin ningún flag activo.
                    const counts = countAlertStates(deviceVariables);

                    return {
                        ...device,
                        high_count: counts.high_count,
                        medium_count: counts.medium_count,
                        low_count: counts.low_count,
                        variables: deviceVariables,
                        last_message: new Date().toISOString()
                    };
                }
                return device;
            });

            // Solo actualizar si hay cambios
            const hasChanges = updatedData.some((d, i) =>
                d.high_count !== prevData[i]?.high_count ||
                d.medium_count !== prevData[i]?.medium_count ||
                d.low_count !== prevData[i]?.low_count ||
                d.last_message !== prevData[i]?.last_message
            );

            return hasChanges ? updatedData : prevData;
        });
    }, [mqttData]);

    useEffect(() => {
        if (!device_user_id) return;
        let isMounted = true;
        axios.get('device/?machine=' + device_user_id).then((response) => {
            if (isMounted) setData(response.data);
        });
        axios.get('billing/overview/').then((res) => {
            const rate = res.data?.subscription?.payment_plan?.refresh_rate_seconds;
            if (isMounted && rate) setPlanRefreshRate(rate);
        }).catch(() => {});
        return () => { isMounted = false; };
    }, [device_user_id])

    const navigationHeader = () => {
        return (
            <div className={'d-flex'}>
                <div style={{cursor: 'pointer'}} onClick={() => navigate('/resources')}>{device_user?.name}</div>
                <div className={'px-1'}>/</div>
                <div>{t('devices.title')}</div>
            </div>
        );
    }

    return (
        <div className={styles.dashboard_container}>
            <Header/>

            <DeviceModal show={showModal} setter={setShowModal}/>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={navigationHeader()} openModal={setShowModal} back={'/resources'} createName={'Device'}>
                    <div className={`d-flex flex-row rounded-2 me-3 mb-2 mt-2 ${styles.mapToggle}`}>
                        <div onClick={() => setViewType('list')} className={`${viewType === 'list' ? styles.selected : styles.unselected}`}>List</div>
                        <div className={styles.separator}/>
                        <div onClick={() => setViewType('map')} className={`${viewType === 'map' ? styles.selected : styles.unselected}`}>{t('gps.tabs.map')}</div>
                    </div>
                </PageTitle>

                {viewType === 'list' ?
                    (data.length > 0 ?
                            <ListView data={data} planRefreshRate={planRefreshRate}/>
                            :
                            <EmptyPlaceholder type={'devices'}/>
                    )
                    :
                    <MapView data={data}/>
                }
            </div>
        </div>
    );
}
